import {
	Badge,
	Button,
	Flex,
	NumberInput,
	SegmentedControl,
	Stack,
	Text,
	Textarea,
	TextInput,
} from '@mantine/core'
import { IconPlayerPlay } from '@tabler/icons-react'
import dayjs from 'dayjs'
import React, { useCallback, useMemo, useState } from 'react'
import { MRT_ColumnDef } from 'mantine-react-table'
import { runSystemScript } from 'src/apis/settingAPI'
import CustomDatePickerInput from 'src/components/CustomComponents/CustomDatePickerInput'
import {
	getListUser,
	getUserDevices,
	syncCountryForSearchInApp,
} from 'src/apis/userAPI'
import CustomProgress from 'src/components/CustomComponents/CustomProgress'
import TableComponent from 'src/components/TableComponent'
import { useModal } from 'src/context/ModalContext'
import { color, formatDate, formatDateTime, size } from 'src/variable/constant'
import CustomTooltip from 'src/components/CustomComponents/CustomTooltip'
import { convertPhoneNumber } from 'src/utils/utils'
import { mapDailyReportResultToProgress } from './dailyReportProgress'

type ScriptProgress = {
	total: number
	processed: number
	updated: number
	skipped: number
	failed: number
	percent: number
	status: string
	currentUserId?: string
	message?: string
}

type ScriptRunResult = ScriptProgress & {
	id: string
	no: number
	script_name: string
	started_at: string
	finished_at: string
	duration: string
	message: string
	status: 'completed' | 'warning' | 'failed'
}

type ScriptInput = {
	limit?: number
	date?: Date | null
	user?: string
	migrationMode?: 'dry-run' | 'apply'
	pushMode?: 'user' | 'token'
	search?: string
	selectedUser?: any
	title?: string
	content?: string
	fcmTokens?: string
}

type ScriptInputConfig =
	| {
			type: 'limit'
			label: string
			defaultValue: number
			min: number
	  }
	| {
			type: 'daily-report'
			dateLabel: string
			userLabel: string
	  }
	| {
			type: 'push-message'
	  }
	| {
			type: 'country-code-migration'
	  }

type ScriptConfig = {
	id: string
	name: string
	description: string
	type: 'sse' | 'json'
	run: any
	confirmLabel: string | ((input: ScriptInput) => string)
	input?: ScriptInputConfig
}

const initialProgress: ScriptProgress = {
	total: 0,
	processed: 0,
	updated: 0,
	skipped: 0,
	failed: 0,
	percent: 0,
	status: '',
}

const formatDuration = (startedAt: number, finishedAt: number) => {
	const durationSeconds = Math.max(0, Math.round((finishedAt - startedAt) / 1000))
	const minutes = Math.floor(durationSeconds / 60)
	const seconds = durationSeconds % 60
	return `${minutes}m ${seconds}s`
}

const PUSH_MESSAGE_SCRIPT_ID = 'push-message'
const USER_COUNTRY_CODE_SCRIPT_ID = 'user-country-codes'

const parseFcmTokens = (value?: string) =>
	Array.from(
		new Set(
			String(value || '')
				.split(/[\s,;]+/)
				.map((token) => token.trim())
				.filter(Boolean),
		),
	)

const scripts: ScriptConfig[] = [
	{
		id: 'sync-country-for-search-in-app',
		name: 'Sync country for search in app',
		description:
			'Backfill country for users who shared local address and coordinates but do not have country data yet.',
		type: 'sse',
		run: syncCountryForSearchInApp,
		confirmLabel:
			'Would you like to sync country for search in app? This script will update users missing country data.',
	},
	{
		id: 'migrate-open-hangout',
		name: 'Migrate bật hangout',
		description:
			'Turn on Hangout for the latest online users that have not opened Hangout yet.',
		type: 'json',
		run: runSystemScript,
		input: {
			type: 'limit',
			label: 'Limit',
			defaultValue: 25,
			min: 1,
		},
		confirmLabel: (input) =>
			`Would you like to turn on Hangout for ${input.limit || 25} users?`,
	},
	{
		id: USER_COUNTRY_CODE_SCRIPT_ID,
		name: 'Migrate user country codes',
		description:
			'Validate ISO country codes, normalize valid values, recover from country names, and clear unresolved invalid values.',
		type: 'json',
		run: runSystemScript,
		input: {
			type: 'country-code-migration',
		},
		confirmLabel: (input) =>
			input.migrationMode === 'apply'
				? 'Apply country code migration? This will update tbl_users.country_code. Run Dry-run and review the result first.'
				: 'Run country code migration in Dry-run mode? No database records will be updated.',
	},
	{
		id: PUSH_MESSAGE_SCRIPT_ID,
		name: 'Push message',
		description:
			'Push a simple PUSH_BY_ADMIN notification to a selected user or pasted FCM token.',
		type: 'json',
		run: runSystemScript,
		input: {
			type: 'push-message',
		},
		confirmLabel: (input) => {
			const mode = input.pushMode || 'user'
			const tokenCount =
				mode === 'token'
					? parseFcmTokens(input.fcmTokens).length
					: input.selectedUser
						? 1
						: 0
			const targetText =
				mode === 'user'
					? `${input.selectedUser?.name || input.selectedUser?.phone || 'selected user'} (${input.selectedUser?.id || '-'})`
					: `${tokenCount} FCM token(s)`
			return `Would you like to push "${input.title || '-'}" to ${targetText}?`
		},
	},
	{
		id: 'daily-git-jira-report',
		name: 'Daily Git/Jira report',
		description:
			'Send Git/Jira daily report to Telegram by date, optionally filtered by user email, display name, or author key.',
		type: 'json',
		run: runSystemScript,
		input: {
			type: 'daily-report',
			dateLabel: 'Date',
			userLabel: 'User(s)',
		},
		confirmLabel: (input) => {
			const dateText = input.date
				? dayjs(input.date).format('YYYY-MM-DD')
				: 'today'
			const userText = input.user?.trim() || 'all users'
			return `Would you like to send Daily Git/Jira report for ${dateText} (${userText})?`
		},
	},
]

function ScriptRunPage() {
	const { openError, openModal, closeModal } = useModal()
	const [runningScriptId, setRunningScriptId] = useState<string>('')
	const [progress, setProgress] = useState<ScriptProgress>(initialProgress)
	const [resultRows, setResultRows] = useState<ScriptRunResult[]>([])
	const [scriptInputs, setScriptInputs] = useState<Record<string, ScriptInput>>({
		'migrate-open-hangout': { limit: 25 },
		[USER_COUNTRY_CODE_SCRIPT_ID]: { migrationMode: 'dry-run' },
		[PUSH_MESSAGE_SCRIPT_ID]: { pushMode: 'user' },
	})
	const [pushUsers, setPushUsers] = useState<any[]>([])
	const [pushDevices, setPushDevices] = useState<any[]>([])
	const [loadingPushUsers, setLoadingPushUsers] = useState(false)
	const [loadingPushDevices, setLoadingPushDevices] = useState(false)

	const isRunning = !!runningScriptId
	const runningScript = scripts.find((script) => script.id === runningScriptId)
	const dailyReportMinDate = useMemo(
		() => dayjs().subtract(7, 'day').startOf('day').toDate(),
		[],
	)
	const dailyReportMaxDate = useMemo(() => new Date(), [])

	const columns = useMemo<MRT_ColumnDef[]>(
		() => [
			{
				accessorKey: 'no',
				header: 'No',
				size: size.zero,
			},
			{
				accessorKey: 'script_name',
				header: 'Script name',
				size: size.medium,
			},
			{
				accessorKey: 'status',
				header: 'Status',
				size: size.text,
				Cell: ({ cell }: any) => {
					const value = cell.getValue()
					const badgeColor =
						value === 'completed'
							? 'green'
							: value === 'warning'
								? 'yellow'
								: 'red'
					return (
						<Badge color={badgeColor}>{value}</Badge>
					)
				},
			},
			{
				accessorKey: 'total',
				header: 'Total',
				size: size.zero,
			},
			{
				accessorKey: 'processed',
				header: 'Processed',
				size: size.zero,
			},
			{
				accessorKey: 'updated',
				header: 'Updated',
				size: size.zero,
			},
			{
				accessorKey: 'skipped',
				header: 'Skipped',
				size: size.zero,
			},
			{
				accessorKey: 'failed',
				header: 'Failed',
				size: size.zero,
			},
			{
				accessorKey: 'started_at',
				header: 'Started at',
				size: size.text,
			},
			{
				accessorKey: 'finished_at',
				header: 'Finished at',
				size: size.text,
			},
			{
				accessorKey: 'duration',
				header: 'Duration',
				size: size.text,
			},
			{
				accessorKey: 'message',
				header: 'Message/error',
				size: size.medium,
			},
		],
		[],
	)

	const getScriptInput = useCallback(
		(script: ScriptConfig) => ({
			limit:
				scriptInputs[script.id]?.limit ??
				(script.input?.type === 'limit'
					? script.input.defaultValue
					: undefined),
			date: scriptInputs[script.id]?.date || null,
			user: scriptInputs[script.id]?.user || '',
			migrationMode:
				scriptInputs[script.id]?.migrationMode || 'dry-run',
			pushMode: scriptInputs[script.id]?.pushMode || 'user',
			search: scriptInputs[script.id]?.search || '',
			selectedUser: scriptInputs[script.id]?.selectedUser || null,
			title: scriptInputs[script.id]?.title || '',
			content: scriptInputs[script.id]?.content || '',
			fcmTokens: scriptInputs[script.id]?.fcmTokens || '',
		}),
		[scriptInputs],
	)

	const isScriptValid = useCallback(
		(script: ScriptConfig) => {
			if (script.input?.type === 'push-message') {
				const input = getScriptInput(script)
				if (!input.title?.trim() || !input.content?.trim()) return false
				if (input.pushMode === 'token') {
					return parseFcmTokens(input.fcmTokens).length > 0
				}
				return !!input.selectedUser?.id
			}
			if (script.input?.type !== 'limit') return true
			const limit = getScriptInput(script).limit
			return (
				typeof limit === 'number' &&
				Number.isFinite(limit) &&
				limit >= script.input.min
			)
		},
		[getScriptInput],
	)

	const addResultRow = useCallback(
		(params: {
			scriptName: string
			status: ScriptRunResult['status']
			startedAt: number
			finishedAt: number
			progressData: ScriptProgress
			message?: string
		}) => {
			const nextRow: ScriptRunResult = {
				...params.progressData,
				id: `${params.startedAt}-${params.status}`,
				no: 1,
				script_name: params.scriptName,
				started_at: dayjs(params.startedAt).format(formatDateTime),
				finished_at: dayjs(params.finishedAt).format(formatDateTime),
				duration: formatDuration(params.startedAt, params.finishedAt),
				status: params.status,
				message: params.message || params.progressData.message || '-',
			}
			setResultRows([nextRow])
		},
		[],
	)

	const mapHangoutResultToProgress = useCallback((result: any) => {
		const count = Number(result?.count || 0)
		const updatedCount = Number(result?.updated_count || 0)
		const usersLength = Array.isArray(result?.users) ? result.users.length : 0
		return {
			total: count,
			processed: count,
			updated: updatedCount,
			skipped: Math.max(count - updatedCount, 0),
			failed: 0,
			percent: 100,
			status: 'completed',
			message: `Updated ${updatedCount}/${count} users. Returned users: ${usersLength}`,
		}
	}, [])

	const mapPushMessageResultToProgress = useCallback((result: any) => {
		const tokenCount = Number(result?.token_count || 0)
		const successCount = Number(result?.success_count || 0)
		const failureCount = Number(result?.failure_count || 0)
		const mode = result?.mode || '-'
		const target =
			mode === 'user'
				? result?.user_id || '-'
				: `${tokenCount} pasted token(s)`

		return {
			total: tokenCount,
			processed: tokenCount,
			updated: successCount,
			skipped: 0,
			failed: failureCount,
			percent: 100,
			status: result?.sent ? 'completed' : 'completed',
			message:
				result?.message ||
				`Mode: ${mode} | Target: ${target} | Sent: ${successCount}/${tokenCount} | Failed: ${failureCount}`,
		}
	}, [])

	const mapCountryCodeResultToProgress = useCallback((result: any) => {
		const total = Number(result?.total || 0)
		const keep = Number(result?.keep || 0)
		const normalize = Number(result?.normalize || 0)
		const fixFromCountry = Number(result?.fix_from_country || 0)
		const setNull = Number(result?.set_null || 0)
		const updated = Number(result?.updated || 0)
		const mode = result?.mode || 'dry-run'
		const samples = Array.isArray(result?.samples)
			? result.samples
					.map(
						(sample) =>
							`${sample.id}: ${sample.from || 'null'} → ${sample.to || 'null'} (${sample.action})`,
					)
					.join('; ')
			: ''

		return {
			total,
			processed: total,
			updated,
			skipped: keep,
			failed: 0,
			percent: 100,
			status: 'completed',
			message: [
				`Mode: ${mode}`,
				`Keep: ${keep}`,
				`Normalize: ${normalize}`,
				`Fix from country: ${fixFromCountry}`,
				`Set null: ${setNull}`,
				samples ? `Samples: ${samples}` : '',
			]
				.filter(Boolean)
				.join(' | '),
		}
	}, [])

	const updatePushInput = useCallback((patch: Partial<ScriptInput>) => {
		setScriptInputs((pre) => ({
			...pre,
			[PUSH_MESSAGE_SCRIPT_ID]: {
				...pre[PUSH_MESSAGE_SCRIPT_ID],
				...patch,
			},
		}))
	}, [])

	const activePushDevices = useMemo(
		() =>
			pushDevices.filter(
				(device) =>
					device?.fcm_token && !device?.logout_time && !device?.deleted_at,
			),
		[pushDevices],
	)

	const handleSearchPushUsers = useCallback(async () => {
		const input = scriptInputs[PUSH_MESSAGE_SCRIPT_ID] || {}
		const keyword = input.search?.trim()
		if (!keyword) {
			setPushUsers([])
			return
		}

		setLoadingPushUsers(true)
		try {
			const phoneValues = keyword.startsWith('0')
				? convertPhoneNumber(keyword)
				: [keyword]
			const res = await getListUser({
				fields: ['$all', '$paranoid'],
				page: 1,
				limit: 8,
				where: {
					account_type: 'USER',
					$or: [
						{ name: { $iLike: `%${keyword}%` } },
						...phoneValues.map((phone) => ({
							phone: { $iLike: `%${phone}%` },
						})),
					],
				},
			})
			setPushUsers(res?.data?.results?.objects?.rows || [])
		} catch (error) {
			openError(error)
		} finally {
			setLoadingPushUsers(false)
		}
	}, [openError, scriptInputs])

	const handleSelectPushUser = useCallback(
		async (user: any) => {
			updatePushInput({ selectedUser: user })
			setPushDevices([])
			setLoadingPushDevices(true)
			try {
				const res = await getUserDevices(user.id, {
					fields: ['$all', '$paranoid'],
					page: 1,
					limit: 20,
				})
				setPushDevices(res?.data?.results?.objects?.rows || [])
			} catch (error) {
				openError(error)
			} finally {
				setLoadingPushDevices(false)
			}
		},
		[openError, updatePushInput],
	)

	const handleRunScript = useCallback(
		async (script: (typeof scripts)[number]) => {
			closeModal()
			const startedAt = Date.now()
			let latestProgress: ScriptProgress = {
				...initialProgress,
				status: 'processing',
			}
			setProgress(latestProgress)
			setRunningScriptId(script.id)

			try {
				if (script.type === 'sse') {
					await script.run(({ event, data }) => {
						if (event === 'progress') {
							latestProgress = {
								...latestProgress,
								...data,
							}
							setProgress(latestProgress)
						}

						if (event === 'completed') {
							latestProgress = {
								...latestProgress,
								...data,
								percent: 100,
								status: 'completed',
							}
							setProgress(latestProgress)
						}

						if (event === 'error') {
							throw new Error(data?.message || 'Run script failed')
						}
					})
				} else if (script.id === 'migrate-open-hangout') {
					const input = getScriptInput(script)
					const res = await script.run({
						command: 'hangout',
						limit: input.limit,
					})
					const result = res?.data?.results?.object || {}
					latestProgress = mapHangoutResultToProgress(result)
					setProgress(latestProgress)
				} else if (script.id === 'daily-git-jira-report') {
					const input = getScriptInput(script)
					const res = await script.run({
						command: 'daily-report',
						...(input.date
							? { date: dayjs(input.date).format('YYYY-MM-DD') }
							: {}),
						...(input.user?.trim()
							? { user: input.user.trim() }
							: {}),
					})
					const result = res?.data?.results?.object || {}
					latestProgress = mapDailyReportResultToProgress(result)
					setProgress(latestProgress)
				} else if (script.id === PUSH_MESSAGE_SCRIPT_ID) {
					const input = getScriptInput(script)
					const mode = input.pushMode || 'user'
					const res = await script.run({
						command: 'push-message',
						mode,
						title: input.title?.trim(),
						content: input.content?.trim(),
						...(mode === 'user'
							? { user_id: input.selectedUser?.id }
							: { fcm_tokens: parseFcmTokens(input.fcmTokens) }),
					})
					const result = res?.data?.results?.object || {}
					latestProgress = mapPushMessageResultToProgress(result)
					setProgress(latestProgress)
				} else if (script.id === USER_COUNTRY_CODE_SCRIPT_ID) {
					const input = getScriptInput(script)
					const res = await script.run({
						command: 'user-country-codes',
						mode: input.migrationMode || 'dry-run',
					})
					const result = res?.data?.results?.object || {}
					latestProgress = mapCountryCodeResultToProgress(result)
					setProgress(latestProgress)
				}

				const finishedAt = Date.now()
				const finalStatus: ScriptRunResult['status'] =
					latestProgress.status === 'warning'
						? 'warning'
						: latestProgress.status === 'failed'
							? 'failed'
							: 'completed'
				addResultRow({
					scriptName: script.name,
					status: finalStatus,
					startedAt,
					finishedAt,
					progressData: latestProgress,
					message: latestProgress.message || 'Completed',
				})
				if (finalStatus === 'warning') {
					openModal({
						type: 'warning',
						label: latestProgress.message || `${script.name} completed with warnings`,
					})
				} else if (finalStatus === 'failed') {
					openModal({
						type: 'error',
						label: latestProgress.message || `${script.name} failed`,
					})
				} else {
					openModal({
						type: 'success',
						label: `${script.name} completed successfully`,
					})
				}
			} catch (error) {
				const finishedAt = Date.now()
				const failedProgress = {
					...latestProgress,
					status: 'failed',
					message: error?.message || 'Run script failed',
				}
				setProgress(failedProgress)
				addResultRow({
					scriptName: script.name,
					status: 'failed',
					startedAt,
					finishedAt,
					progressData: failedProgress,
					message: failedProgress.message,
				})
				openError(error)
			} finally {
				setRunningScriptId('')
			}
		},
		[
			addResultRow,
			closeModal,
			getScriptInput,
			mapHangoutResultToProgress,
			mapCountryCodeResultToProgress,
			mapPushMessageResultToProgress,
			openError,
			openModal,
		],
	)

	const handleConfirmRunScript = useCallback(
		(script: (typeof scripts)[number]) => {
			const input = getScriptInput(script)
			const label =
				typeof script.confirmLabel === 'function'
					? script.confirmLabel(input)
					: script.confirmLabel
			openModal({
				type: 'confirm',
				label: <div>{label}</div>,
				handleConfirm: () => handleRunScript(script),
			})
		},
		[getScriptInput, handleRunScript, openModal],
	)

	return (
		<Stack sx={{ width: '100%' }} spacing="md">
			<Text fw={700} size="lg">
				Script run
			</Text>

			<Stack spacing="sm">
				{scripts.map((script) => (
					<Flex
						key={script.id}
						direction={script.input?.type === 'push-message' ? 'column' : 'row'}
						align={script.input?.type === 'push-message' ? 'stretch' : 'center'}
						justify="space-between"
						gap={16}
						sx={{
							border: '1px solid #e6ebe7',
							borderRadius: 8,
							padding: 16,
							background: '#ffffff',
						}}
					>
						<Stack spacing={4} sx={{ minWidth: 0 }}>
							<Text fw={700}>{script.name}</Text>
							<Text size="sm" color="dimmed">
								{script.description}
							</Text>
						</Stack>
						<Flex align="flex-end" gap={12}>
							{script.input?.type === 'limit' && (
								<NumberInput
									label={script.input.label}
									min={script.input.min}
									step={1}
									precision={0}
									value={getScriptInput(script).limit}
									onChange={(value) =>
										setScriptInputs((pre) => ({
											...pre,
											[script.id]: {
												...pre[script.id],
												limit: Number(value || 0),
											},
										}))
									}
									sx={{ width: 140 }}
								/>
							)}
							{script.input?.type === 'daily-report' && (
								<>
									<CustomDatePickerInput
										clearable
										label={script.input.dateLabel}
										placeholder="DD/MM/YYYY"
										valueFormat={formatDate}
										value={getScriptInput(script).date}
										minDate={dailyReportMinDate}
										maxDate={dailyReportMaxDate}
										onChange={(value) =>
											setScriptInputs((pre) => ({
												...pre,
												[script.id]: {
													...pre[script.id],
													date: value,
												},
											}))
										}
										sx={{ width: 180 }}
									/>
									<TextInput
										label={script.input.userLabel}
										placeholder="email, name, authorKey"
										value={getScriptInput(script).user}
										onChange={(event) => {
											const value = event.currentTarget.value
											setScriptInputs((pre) => ({
												...pre,
												[script.id]: {
													...pre[script.id],
													user: value,
												},
											}))
										}}
										sx={{ width: 260 }}
									/>
								</>
							)}
							{script.input?.type === 'country-code-migration' && (
								<SegmentedControl
									value={getScriptInput(script).migrationMode}
									data={[
										{ label: 'Dry-run', value: 'dry-run' },
										{ label: 'Apply', value: 'apply' },
									]}
									onChange={(value) =>
										setScriptInputs((pre) => ({
											...pre,
											[script.id]: {
												...pre[script.id],
												migrationMode: value as 'dry-run' | 'apply',
											},
										}))
									}
								/>
							)}
							{script.input?.type === 'push-message' && (
								<Stack spacing={10} sx={{ minWidth: 640, maxWidth: 980 }}>
									<SegmentedControl
										value={getScriptInput(script).pushMode}
										data={[
											{ label: 'User', value: 'user' },
											{ label: 'FCM token', value: 'token' },
										]}
										onChange={(value) => {
											updatePushInput({
												pushMode: value as 'user' | 'token',
												selectedUser: null,
											})
											setPushDevices([])
										}}
										sx={{ width: 220 }}
									/>

									{getScriptInput(script).pushMode === 'user' && (
										<Stack spacing={8}>
											<Flex align="flex-end" gap={8}>
												<TextInput
													label="Search user"
													placeholder="Phone or name"
													value={getScriptInput(script).search}
													onChange={(event) => {
														updatePushInput({
															search: event.currentTarget.value,
															selectedUser: null,
														})
														setPushDevices([])
													}}
													onKeyDown={(event) => {
														if (event.key === 'Enter') {
															handleSearchPushUsers()
														}
													}}
													sx={{ width: 320 }}
												/>
												<Button
													loading={loadingPushUsers}
													onClick={handleSearchPushUsers}
													sx={{
														background: color.CmsPrimary,
														':hover': {
															background: `${color.CmsPrimary} !important`,
														},
													}}
												>
													Search
												</Button>
											</Flex>

											{pushUsers.length > 0 && (
												<Stack spacing={6}>
													{pushUsers.map((user) => {
														const selected =
															getScriptInput(script).selectedUser?.id === user.id
														return (
															<Flex
																key={user.id}
																align="center"
																justify="space-between"
																gap={12}
																onClick={() => handleSelectPushUser(user)}
																sx={{
																	cursor: 'pointer',
																	border: `1px solid ${
																		selected ? color.CmsPrimary : '#e6ebe7'
																	}`,
																	borderRadius: 6,
																	padding: '8px 10px',
																	background: selected ? '#f0fdf4' : '#ffffff',
																}}
															>
																<Stack spacing={2} sx={{ minWidth: 0 }}>
																	<Text fw={600} size="sm">
																		{user.name || '-'} {user.phone ? `- ${user.phone}` : ''}
																	</Text>
																	<Text
																		size="xs"
																		color="dimmed"
																		sx={{ wordBreak: 'break-all' }}
																	>
																		{user.id}
																	</Text>
																</Stack>
																<Badge color={user.is_accept_notification ? 'green' : 'gray'}>
																	{user.is_accept_notification ? 'accept noti' : 'off noti'}
																</Badge>
															</Flex>
														)
													})}
												</Stack>
											)}

											{getScriptInput(script).selectedUser?.id && (
												<Stack spacing={6}>
													<Text size="sm" fw={700}>
														User ID: {getScriptInput(script).selectedUser.id}
													</Text>
													<Text size="sm" color={activePushDevices.length ? 'dimmed' : 'red'}>
														Active token(s): {activePushDevices.length}
														{loadingPushDevices ? ' (loading...)' : ''}
													</Text>
													{activePushDevices.map((device) => (
														<Stack
															key={device.id}
															spacing={2}
															sx={{
																border: '1px solid #e6ebe7',
																borderRadius: 6,
																padding: 8,
																background: '#fafafa',
															}}
														>
															<Text
																size="xs"
																sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
															>
																{device.fcm_token}
															</Text>
															<Text size="xs" color="dimmed">
																{device.platform || '-'} | {device.version || '-'} |{' '}
																{device.device_name || device.device_model || '-'} | last login:{' '}
																{device.last_login_at
																	? dayjs(Number(device.last_login_at)).format(formatDateTime)
																	: '-'}
															</Text>
														</Stack>
													))}
												</Stack>
											)}
										</Stack>
									)}

									{getScriptInput(script).pushMode === 'token' && (
										<Textarea
											label="FCM token"
											placeholder="Paste one or more FCM tokens, separated by comma, space, or new line"
											minRows={4}
											value={getScriptInput(script).fcmTokens}
											onChange={(event) =>
												updatePushInput({ fcmTokens: event.currentTarget.value })
											}
										/>
									)}

									<Flex gap={12} align="flex-start">
										<TextInput
											label="Title"
											placeholder="Push title"
											value={getScriptInput(script).title}
											onChange={(event) =>
												updatePushInput({ title: event.currentTarget.value })
											}
											sx={{ width: 320 }}
										/>
										<Textarea
											label="Content"
											placeholder="Push content"
											minRows={2}
											value={getScriptInput(script).content}
											onChange={(event) =>
												updatePushInput({ content: event.currentTarget.value })
											}
											sx={{ flex: 1, minWidth: 320 }}
										/>
									</Flex>
								</Stack>
							)}
							<CustomTooltip label="Run script">
								<Button
									leftIcon={<IconPlayerPlay size={16} />}
									disabled={isRunning || !isScriptValid(script)}
									loading={runningScriptId === script.id}
									onClick={() => handleConfirmRunScript(script)}
									sx={{
										background: color.CmsPrimary,
										':hover': {
											background: `${color.CmsPrimary} !important`,
										},
									}}
								>
									Run
								</Button>
							</CustomTooltip>
						</Flex>
					</Flex>
				))}
			</Stack>

			{isRunning && (
				<Stack spacing={4}>
					<Flex justify="space-between" sx={{ fontSize: 13 }}>
						<div>
							Running {runningScript?.name}: {progress.processed}/
							{progress.total}
						</div>
						<div>
							Updated: {progress.updated} | Skipped: {progress.skipped} |
							Failed: {progress.failed}
						</div>
					</Flex>
					<CustomProgress value={progress.percent} />
					{(progress.currentUserId || progress.message) && (
						<Text size="sm" color="dimmed">
							{progress.currentUserId ? `Current user: ${progress.currentUserId}` : ''}
							{progress.currentUserId && progress.message ? ' - ' : ''}
							{progress.message || ''}
						</Text>
					)}
				</Stack>
			)}

			<TableComponent
				columns={columns}
				total={resultRows.length}
				data={resultRows}
				pagination={{ pageIndex: 1, pageSize: 10 }}
				loading={false}
				enableRowActions={false}
				hidePagination
				handleChangePage={() => {}}
			/>
		</Stack>
	)
}

export default React.memo(ScriptRunPage)
